import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Image as ImageIcon, LayoutTemplate } from 'lucide-react';
import { uploadsAbsoluteUrl } from '../../../../utils/uploadsBase';
import MediaSelectModal from '../MediaSelectModal';
import './HeroHomeEditor.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function deepClone(o) {
  try {
    return JSON.parse(JSON.stringify(o || {}));
  } catch {
    return {};
  }
}

function mergeHeroContent(comp, changes) {
  const base =
    typeof comp.content === 'string'
      ? JSON.parse(comp.content || '{}')
      : deepClone(comp.content);
  const ch = changes[comp.id] || {};
  Object.entries(ch).forEach(([pathStr, val]) => {
    if (pathStr === '__mediaId__') return;
    const parts = pathStr.split('.');
    let cur = base;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      const next = parts[i + 1];
      if (cur[p] == null) {
        cur[p] = /^\d+$/.test(next) ? [] : {};
      }
      cur = cur[p];
    }
    cur[parts[parts.length - 1]] = val;
  });
  return base;
}

function ensureLocaleShape(content, langCodes) {
  langCodes.forEach((code) => {
    if (!content[code] || typeof content[code] !== 'object') content[code] = {};
    const b = content[code].buttons;
    if (!Array.isArray(b) || b.length < 2) {
      const prev = Array.isArray(b) ? [...b] : [];
      while (prev.length < 2) prev.push({ label: '', href: '' });
      content[code].buttons = prev;
    }
  });
}

/**
 * Редактор шапки главной: заголовок / подзаголовок × 3 языка, 2 кнопки (текст+ссылка × языки), одна картинка из медиатеки.
 */
export default function HeroHomeEditor({
  component: comp,
  languages,
  changes,
  onUpdate,
  token,
}) {
  const langCodes = (languages || []).map((l) => l.code);
  const content = useMemo(() => {
    const m = mergeHeroContent(comp, changes);
    ensureLocaleShape(m, langCodes.length ? langCodes : ['en', 'ru', 'uz']);
    return m;
  }, [comp, changes, langCodes]);

  const compChanges = changes[comp.id] || {};
  const mediaId = Object.prototype.hasOwnProperty.call(compChanges, '__mediaId__')
    ? compChanges.__mediaId__
    : comp.mediaId;

  const [mediaModalOpen, setMediaModalOpen] = useState(false);

  const { data: mediaList = [] } = useQuery({
    queryKey: ['adminMediaList', token],
    queryFn: async () => {
      const r = await axios.get(`${API_URL}/admin/media`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return r.data.media || [];
    },
    enabled: !!token,
    staleTime: 60_000,
  });

  const selectedMedia =
    mediaList.find((m) => m.id === mediaId) || comp.media || null;
  const previewUrl = selectedMedia?.url
    ? uploadsAbsoluteUrl(selectedMedia.url, API_URL)
    : null;

  const setField = (lang, field, value) => {
    onUpdate(comp.id, [lang, field], value);
  };

  const setBtn = (lang, index, field, value) => {
    onUpdate(comp.id, [lang, 'buttons', String(index), field], value);
  };

  const setMediaId = (id) => {
    onUpdate(comp.id, ['__mediaId__'], id === '' ? null : id);
  };

  const labels = {
    heading: 'Заголовок (Header)',
    subheading: 'Подзаголовок',
    image: 'Фон Hero (общее для всех языков)',
    btn1: 'Кнопка 1',
    btn2: 'Кнопка 2',
    text: 'Текст',
    link: 'Ссылка',
  };

  return (
    <div className="hero-home-editor">
      <div className="hero-home-editor__head">
        <LayoutTemplate size={22} />
        <div>
          <h3 className="hero-home-editor__title">Шапка главной (Hero)</h3>
          <p className="hero-home-editor__hint">
            Тексты — на каждый язык; изображение одно на все локали (медиатека).
          </p>
        </div>
      </div>

      <section className="hero-home-section">
        <h4>{labels.heading}</h4>
        <div className="hero-lang-grid">
          {langCodes.map((code) => (
            <label key={code} className="hero-lang-field">
              <span className="hero-lang-code">{code.toUpperCase()}</span>
              <input
                type="text"
                value={content[code]?.heading ?? ''}
                onChange={(e) => setField(code, 'heading', e.target.value)}
                placeholder={`Заголовок (${code})`}
              />
            </label>
          ))}
        </div>
      </section>

      <section className="hero-home-section">
        <h4>{labels.subheading}</h4>
        <div className="hero-lang-grid">
          {langCodes.map((code) => (
            <label key={code} className="hero-lang-field">
              <span className="hero-lang-code">{code.toUpperCase()}</span>
              <textarea
                rows={2}
                value={content[code]?.subheading ?? ''}
                onChange={(e) => setField(code, 'subheading', e.target.value)}
                placeholder={`Подзаголовок (${code})`}
              />
            </label>
          ))}
        </div>
      </section>

      <section className="hero-home-section hero-home-section--media">
        <h4>
          <ImageIcon size={18} /> {labels.image}
        </h4>
        <div className="hero-media-row">
          {previewUrl ? (
            <div className="hero-media-preview">
              <img src={previewUrl} alt="" />
              <button
                type="button"
                className="hero-media-clear"
                onClick={() => setMediaId(null)}
              >
                Сбросить
              </button>
            </div>
          ) : (
            <div className="hero-media-empty">Файл не выбран</div>
          )}
          <div className="hero-media-select-wrap">
            <label className="hero-media-label">Медиатека</label>
            <button
              type="button"
              className="hero-media-open-modal"
              onClick={() => setMediaModalOpen(true)}
            >
              Открыть галерею…
            </button>
            <p className="hero-media-modal-hint">
              Сетка превью — выберите файл визуально.
            </p>
          </div>
        </div>
      </section>

      <MediaSelectModal
        open={mediaModalOpen}
        onClose={() => setMediaModalOpen(false)}
        token={token}
        apiUrl={API_URL}
        selectedId={mediaId}
        onSelect={setMediaId}
        title="Фон Hero — выбор изображения"
      />

      {[0, 1].map((btnIdx) => (
        <section key={btnIdx} className="hero-home-section">
          <h4>
            {btnIdx === 0 ? labels.btn1 : labels.btn2} (журнал / конференция)
          </h4>
          <div className="hero-button-block">
            {langCodes.map((code) => (
              <div key={code} className="hero-button-lang">
                <span className="hero-lang-code">{code.toUpperCase()}</span>
                <input
                  type="text"
                  value={content[code]?.buttons?.[btnIdx]?.label ?? ''}
                  onChange={(e) =>
                    setBtn(code, btnIdx, 'label', e.target.value)
                  }
                  placeholder={labels.text}
                />
                <input
                  type="text"
                  value={content[code]?.buttons?.[btnIdx]?.href ?? ''}
                  onChange={(e) =>
                    setBtn(code, btnIdx, 'href', e.target.value)
                  }
                  placeholder="/path или полный URL"
                />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
